import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Radio } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { usePoll } from '@/hooks/use-realtime'
import type { Activity, LivePresentationSession, Profile } from '@/lib/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * "Ada is presenting Git Overview — join?"
 *
 * The activity screen has had a Join button since 0020, but it only helps
 * someone who already navigated to that one topic out of twenty. This is the
 * other half: anyone signed into the team hears about a session wherever they
 * are in the app.
 *
 * ## Why this polls instead of being pushed
 *
 * It would be nicer as a push, and this platform can't. `postgres_changes` and
 * broadcast are both dead here — measured, not assumed; see the table in
 * use-presence.ts. Presence *does* work, but presence is per-channel and a
 * would-be attendee doesn't know the session id to join its channel, so
 * announcing over presence would mean every signed-in user holding an extra
 * team-wide channel and the presenter publishing to two. That's the natural
 * upgrade if the delay ever grates; the row in the database is what makes the
 * simple version correct in the meantime.
 *
 * ## Interval
 *
 * 15s. The activity screen's own poll uses 10s and documents the reason to stay
 * loose — every member of every team is hitting a shared PostgREST that answers
 * 503 when it runs out of connections. This one runs on EVERY page for every
 * signed-in user, so it's the more expensive of the two and gets the longer
 * interval. Being up to 15s late to a talk that runs half an hour costs nothing;
 * `usePoll` also parks the timer entirely while the tab is hidden, which is
 * where most of these page-loads will sit.
 */

/** Dismissals survive a reload — a refresh should not re-ask a declined invite. */
const DISMISS_KEY = 'teamaccel-presentation-invites-dismissed'

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    // Corrupt or unavailable storage must not take the whole app shell down.
    return []
  }
}

function pushDismissed(sessionId: string) {
  try {
    // Capped: a long-running tenant would otherwise grow this forever, and only
    // recent sessions can ever be live enough to re-offer.
    const next = [...readDismissed().filter((s) => s !== sessionId), sessionId].slice(-30)
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next))
  } catch {
    /* best effort */
  }
}

export function PresentationInvite() {
  const { currentTeam, session } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const navigate = useNavigate()

  // Held in state as well as localStorage so declining re-renders immediately
  // rather than waiting for the next poll to notice.
  const [dismissed, setDismissed] = React.useState<string[]>(() => readDismissed())

  const { data, refetch } = useQuery(async () => {
    if (!teamId) return null
    const { data: rows, error: err } = await sb()
      .from('live_presentation_sessions')
      .select('*')
      .eq('team_id', teamId)
      .order('started_at', { ascending: false })
      .limit(1)
    if (err) throw err
    const live = ((rows ?? []) as LivePresentationSession[])[0] ?? null
    if (!live) return null

    // Two small follow-ups rather than an embed: `live_presentation_sessions` is
    // a view, and PostgREST can't infer foreign keys through one, so
    // `select('*, activities(title)')` returns an error rather than a join.
    const [act, prof] = await Promise.all([
      sb().from('activities').select('id, title').eq('id', live.activity_id).maybeSingle(),
      live.presenter_id
        ? sb().from('profiles').select('display_name, email').eq('user_id', live.presenter_id).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ])
    if (act.error) throw act.error

    const p = prof.data as Pick<Profile, 'display_name' | 'email'> | null
    return {
      live,
      title: (act.data as Pick<Activity, 'id' | 'title'> | null)?.title ?? 'a topic',
      presenter: p?.display_name || p?.email || null,
    }
  }, [teamId])

  usePoll(() => void refetch(), 15_000, Boolean(teamId))

  const invite = data?.live ?? null
  // Never invite the presenter to their own session, and never re-ask after a
  // decline. Someone already inside a deck can't see this at all — the deck
  // route renders outside AppShell, so this component isn't mounted there.
  const show = Boolean(
    invite && invite.presenter_id !== userId && !dismissed.includes(invite.id),
  )

  function decline() {
    if (!invite) return
    pushDismissed(invite.id)
    setDismissed(readDismissed())
  }

  function join() {
    if (!invite) return
    // Remembered as dismissed too: on the way back out of the deck this
    // component remounts, the session is very likely still live, and without
    // this the person who just left would be immediately re-invited to it.
    pushDismissed(invite.id)
    setDismissed(readDismissed())
    navigate(`/activities/${invite.activity_id}/present?session=${invite.id}`)
  }

  if (!show || !data) return null

  return (
    <Dialog open onOpenChange={(o) => !o && decline()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
            Live now
          </DialogTitle>
          <DialogDescription>
            {data.presenter ? <strong className="font-medium text-foreground">{data.presenter}</strong> : 'Someone'}
            {' is presenting '}
            <strong className="font-medium text-foreground">{data.title}</strong>
            {/* Where the room actually is, when we know — a talk eighteen slides
                in is a different decision from one that just started. */}
            {data.live.current_slide ? ` · on slide ${data.live.current_slide}` : ''}.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={decline}>
            Not now
          </Button>
          <Button onClick={join}>
            <Radio /> Join
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
